// frontend/src/pages/settings/UserDeviceHistoryPage.tsx
//
// Mockup screen 5 — one person, every device that has ever reached their
// account, and what became of each one.
//
// 🔒 THE OUTCOME IS THE POINT. "This device exists" tells an admin nothing; the
// question they are actually asking is whether a machine they do not recognise
// is holding a slot right now, or lost one to something else. So every row is
// labelled with what happened to it, in words, never with a raw column value.
//
// 🔒 A FAILED LOAD MUST NOT DRAW AN EMPTY HISTORY. "No devices have signed in
// to this account yet" is a statement of fact; a request that failed has no
// facts. On failure the page says so and offers a retry, instead of the list.
//
// 🔒 SIGN OUT ONE, OR BOTH (spec §4). An admin who arrives here has usually
// come to deal with ONE device — the unrecognised one in the list. "Sign out
// both slots" as the only control makes them end that person's phone as well,
// for no reason. Each held slot gets its own button; an empty slot gets none.
//
// 🔒 PLATFORM ONLY (P3). No product vocabulary — copied to Sun Stellar unchanged.

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Smartphone, Monitor } from 'lucide-react';
import { api } from '../../utils/api';
import { agoShort } from '../../utils/agoWords';

interface DeviceRow {
  deviceId: string;
  label: string;
  clientVersion: string;
  channel: 'web' | 'apk';
  firstSeenAt: string;
  lastSeenAt: string;
  ipAddress: string | null;
  networkName: string | null;
  outcome: string;
}
interface Slot { channel: 'web' | 'apk'; sessionId: string | null }
interface Person { id: string; name: string; email: string; roleName: string }

const OUTCOME_WORDS: Record<string, string> = {
  holds_slot: 'Holds the slot',
  signed_out_here: 'Signed out',
  taken_over: 'Lost the slot to another device',
  ended_by_admin: 'Signed out by an administrator',
  signed_out_everywhere: 'Signed out everywhere',
  expired: 'Expired on its own',
};

function outcomeWords(outcome: string): string {
  return OUTCOME_WORDS[outcome] ?? OUTCOME_WORDS.expired;
}

/** Green while it holds a slot, red where something ended it, grey where it
 *  simply ran out. The colour is a shortcut to the words, never a replacement. */
function dotColour(outcome: string): string {
  if (outcome === 'holds_slot') return '#2b8062';
  if (outcome === 'expired') return '#a89e8e';
  return '#c34a48';
}

function day(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function UserDeviceHistoryPage() {
  const { userId } = useParams<{ userId: string }>();
  const [person, setPerson] = useState<Person | null>(null);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busySessionId, setBusySessionId] = useState<string | null>(null);
  // 🔒 Not the load failure. The history on screen was read successfully and is
  // still true; what failed is the sign-out, and the slot is probably still held.
  const [signOutFailed, setSignOutFailed] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await api.get(`/sessions/admin/people/${userId}/devices`);
      setPerson(res.data.data.person ?? null);
      setDevices(res.data.data.devices ?? []);
      setSlots(res.data.data.slots ?? []);
      setFailed(false);
    } catch {
      setPerson(null);
      setDevices([]);
      setSlots([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  /** End ONE slot — spec §4, "Sign out one, or both". */
  async function signOutSlot(sessionId: string) {
    setBusySessionId(sessionId);
    setSignOutFailed(false);
    try {
      await api.delete(`/sessions/admin/sessions/${sessionId}`);
      await load();
    } catch {
      setSignOutFailed(true);
    } finally {
      setBusySessionId(null);
    }
  }

  async function signOutBoth() {
    if (!userId) return;
    setBusy(true);
    try {
      await api.post(`/sessions/admin/people/${userId}/sign-out-both`);
      await load();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  const held = slots.filter(s => s.sessionId).length;

  if (loading) {
    return (
      <div className="p-6">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (failed) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-slate-900">Device history</h1>
        <div className="mt-4 rounded-lg border p-4" style={{ borderColor: '#d77b76', background: '#fff5f4' }}>
          <p className="text-sm font-medium" style={{ color: '#9b3936' }}>
            Could not load this device history — try again.
          </p>
          <p className="mt-1 text-xs text-slate-600">
            This is not a report that no device has ever signed in. We simply could not ask.
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-slate-900">
        {person ? `${person.name} — device history` : 'Device history'}
      </h1>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          {[
            person?.email,
            person?.roleName,
            `${devices.length} device${devices.length === 1 ? '' : 's'} ever`,
            `${held} holding slot${held === 1 ? '' : 's'}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {/* 🔒 One button per HELD slot, before the pair — the narrow action is
              the one an admin came here to take. A slot nobody is in has nothing
              to end, so it is not drawn. */}
          {slots
            .filter(s => s.sessionId)
            .map(s => (
              <button
                key={s.channel}
                type="button"
                onClick={() => void signOutSlot(s.sessionId as string)}
                disabled={busySessionId === s.sessionId}
                className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-40"
                style={{ borderColor: '#d77b76', color: 'var(--danger, #c34a48)' }}
              >
                {busySessionId === s.sessionId && <Loader2 className="h-3 w-3 animate-spin" />}
                Sign out the {s.channel === 'apk' ? 'phone' : 'browser'}
              </button>
            ))}
          <button
            type="button"
            onClick={() => void signOutBoth()}
            disabled={busy || held === 0}
            className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-40"
            style={{ borderColor: '#d77b76', color: 'var(--danger, #c34a48)' }}
          >
            {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            Sign out both slots
          </button>
        </div>
      </div>

      {signOutFailed && (
        <div className="mt-3 rounded-lg border p-3" style={{ borderColor: '#efc15d', background: '#fff8df' }}>
          <p className="text-sm font-medium" style={{ color: '#8a6d1f' }}>
            Could not sign that device out — it may still be holding the slot.
          </p>
          <p className="mt-1 text-xs text-slate-600">
            The history below is what we last read successfully. Try the button again.
          </p>
        </div>
      )}

      <div className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white p-4">
        {devices.length === 0 ? (
          <p className="py-2 text-sm text-slate-400">No devices have signed in to this account yet.</p>
        ) : (
          devices.map(d => {
            const Icon = d.channel === 'apk' ? Smartphone : Monitor;
            return (
              <div key={d.deviceId} className="flex items-start gap-3 py-3">
                <span
                  className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: dotColour(d.outcome) }}
                />
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-medium text-slate-900">{d.label}</p>
                    <p className="text-xs text-slate-500">{outcomeWords(d.outcome)}</p>
                  </div>
                  <p className="text-xs text-slate-600">{d.clientVersion}</p>
                  <p className="text-xs text-slate-500">
                    {[d.ipAddress, d.networkName].filter(Boolean).join(' · ')}
                  </p>
                  {/* The mockup reads "Signed in 2h ago · first seen 12 Jun":
                      relative for the recent end, where "how long ago" is the
                      question, absolute for the first sighting, where a date is. */}
                  <p className="text-xs text-slate-400">
                    {`Last used ${agoShort(d.lastSeenAt)} · first seen ${day(d.firstSeenAt)}`}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      <p className="mt-4 rounded-lg border p-3 text-xs text-slate-600" style={{ borderColor: '#efc15d', background: '#fff8df' }}>
        <strong>What counts as a new device.</strong> A phone or browser is recognised by its make, model
        and app or browser version — never by anything the person types. Reinstalling the app or clearing
        browser data makes the same machine look new, so an alert is a reason to ask, not proof of anything.
      </p>
    </div>
  );
}
